/* Which language a visit starts in (the prototype's initialLang(), app.js "Language"):
 *
 *   1. `?lang=<tag>`               — remembered, as a pick in the sheet is
 *   2. the remembered choice        — localStorage `db_lang`
 *   3. the referrer's first path segment, from our own site only (`delaybuddy.com/es/…`,
 *      the translated WordPress pages)
 *   4. `navigator.languages`, in the browser's order
 *   5. English
 *
 * Only a choice is remembered, never a guess (3 and 4). Each candidate goes through
 * `matchLocale`, which maps a browser or URL tag to one of the 53 locales:
 *
 *   | Tag                                   | Locale            |
 *   | ------------------------------------- | ----------------- |
 *   | exact, any case (`de`, `PT-pt`)       | itself            |
 *   | `en-GB`, `de-AT`, `fr-CA`… (regional) | the language      |
 *   | `pt`, `pt-BR`, `pt-AO`…               | `pt-PT`           |
 *   | `zh`, `zh-CN`, `zh-SG`, `zh-Hans-*`   | `zh-Hans`         |
 *   | `zh-TW`, `zh-HK`, `zh-MO`, `zh-Hant-*`| `zh-Hant`         |
 *   | `sr-Cyrl-*`                           | `sr-Cyrl`         |
 *   | `sr`, `sr-RS`, `sr-ME`, `sr-Latn-*`   | `sr-Latn` (1)     |
 *   | `no`, `nn`, `nb-NO`                   | `nb`              |
 *   | `iw` · `in` · `tl`                    | `he` · `id` · `fil` (old and macro codes) |
 *   | `pa-PK`, `pa-Arab` (Shahmukhi)        | none: our `pa` is Gurmukhi |
 *   | anything else                         | none (next candidate) |
 *
 * (1) Serbian is Latin unless Cyrillic is asked for: docs/poa-translations/languages.md B.3 and
 * the prototype's POA.match(). The w7-02 task file said `sr` → `sr-Cyrl`; the prototype and the
 * languages note win (wave 7 README), see the w7-02 report.
 */
import { isLocale, LOCALES, type Locale } from './registry';

export const LANG_KEY = 'db_lang';

const OLD: Record<string, string> = { no: 'nb', nn: 'nb', iw: 'he', in: 'id', tl: 'fil' };

/** A browser or URL language tag → one of our locales, or null. */
export function matchLocale(tag: string | null | undefined): Locale | null {
  const t = String(tag ?? '')
    .trim()
    .replace(/_/g, '-');
  const exact = LOCALES.find((c) => c.toLowerCase() === t.toLowerCase());
  if (exact || !t) return exact ?? null;
  let loc: Intl.Locale;
  try {
    loc = new Intl.Locale(t);
  } catch {
    return null; // not a tag
  }
  const lang = OLD[loc.language] ?? loc.language;
  const { script, region } = loc;
  if (lang === 'zh') return (script ?? loc.maximize().script) === 'Hant' ? 'zh-Hant' : 'zh-Hans';
  if (lang === 'sr') return script === 'Cyrl' ? 'sr-Cyrl' : 'sr-Latn';
  if (lang === 'pt') return 'pt-PT';
  if (lang === 'pa' && (script === 'Arab' || region === 'PK')) return null;
  return isLocale(lang) ? lang : null;
}

export interface DetectEnv {
  search: string;
  stored: string | null;
  referrer: string;
  /** This page's hostname: the referrer counts only from the same site. */
  host: string;
  languages: readonly string[];
}

export type DetectSource = 'query' | 'stored' | 'referrer' | 'browser' | 'default';

const site = (host: string) => host.toLowerCase().replace(/^www\./, '');

/** The first language of a visit and where it came from (the order above). */
export function detectLocale(env: DetectEnv): { locale: Locale; source: DetectSource } {
  const q = matchLocale(new URLSearchParams(env.search).get('lang'));
  if (q) return { locale: q, source: 'query' };
  if (isLocale(env.stored)) return { locale: env.stored, source: 'stored' };
  try {
    const ref = new URL(env.referrer);
    if (site(ref.hostname) === site(env.host)) {
      const m = matchLocale(ref.pathname.split('/')[1]);
      if (m) return { locale: m, source: 'referrer' };
    }
  } catch {
    /* no referrer, or not a URL */
  }
  for (const l of env.languages) {
    const m = matchLocale(l);
    if (m) return { locale: m, source: 'browser' };
  }
  return { locale: 'en', source: 'default' };
}

/** The remembered pick (localStorage, absent in private modes that refuse it). */
export const storedChoice = {
  get(): string | null {
    try {
      return localStorage.getItem(LANG_KEY);
    } catch {
      return null;
    }
  },
  set(l: Locale) {
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      /* the choice lasts this page */
    }
  },
};

/** This page's environment. */
export function browserEnv(): DetectEnv {
  return {
    search: location.search,
    stored: storedChoice.get(),
    referrer: document.referrer,
    host: location.hostname,
    languages: navigator.languages?.length ? navigator.languages : [navigator.language],
  };
}
